import { useState, useEffect } from "react";
import { Modal, Box, TextField, Grid, Typography } from "@mui/material";
import { useSelector } from "react-redux";

import { makeStyles } from "../../../../theme/theme";
import { secondsToTimecode } from "../../TSVideoPlayer/utils";
import TSSecondaryButton from "../../../../components/common/buttons/TSSecondaryButton";
import TSPrimaryButton from "../../../../components/common/buttons/TSPrimaryButton";
import type { IStoryIndexMetadata } from "../../../../store/state/story_authoring/reducer";
import type { RootState } from "../../../../store/state/rootReducer";

const useStyles = makeStyles()(() => ({
  inputNumber: {
    maxWidth: "15px",
    margin: "0px 0px 0px 20px",
    padding: "0px",
    border: "none",
    "&::-webkit-inner-spin-button": {
      opacity: 1,
      cursor: "pointer",
    },
  },
}));

interface CreatChapterModalProps {
  showModal: boolean;
  item?: IStoryIndexMetadata | null;
  onCancel: () => void;
  onEdit?: (title: string, description: string, keywords: string, startTime: number) => void;
}

const CreatChapterModal = ({ showModal, item, onCancel, onEdit }: CreatChapterModalProps) => {
  const { classes } = useStyles();
  const [title, setTitle] = useState(item?.title || "");
  const [description, setDescription] = useState(item?.synopsis || "");
  const [keywords, setKeywords] = useState(item?.keywords || "");
  const [startTime, setStartTime] = useState<number>(item?.time?.start || 0);
  const storyDuration = useSelector((s: RootState) => s.storyAuthoring.duration) || 0;

  useEffect(() => {
    if (item) {
      setTitle(item.title || "");
      setDescription(item.synopsis || "");
      setKeywords(item.keywords || "");
      setStartTime(item.time?.start || 0);
    }
  }, [item, showModal]);

  const clearAllFields = () => {
    setTitle("");
    setDescription("");
    setKeywords("");
  };

  const handleCancel = () => {
    onCancel();
    clearAllFields();
  };

  const handleSave = () => {
    if (title.length > 1) {
      onEdit(title, description, keywords, startTime);
      clearAllFields();
    }
  };

  const onTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number(e.target.value);
    setStartTime(time);
  };

  return (
    <Modal
      open={showModal}
      onClose={handleCancel}
      aria-labelledby="edit-index-chapter-modal"
      aria-describedby="edit-index-chapter-modal"
    >
      <Box sx={{ background: "white", maxWidth: "650px", margin: "5% auto", padding: "24px" }}>
        <Typography
          variant="h3"
          sx={{
            color: "rgba(0, 0, 0, 0.87)",
            fontSize: "20px",
            lineHeight: "32px",
            fontWeight: 500,
            marginBottom: "24px",
          }}
        >
          Edit Chapter
        </Typography>

        <Box sx={{ width: "100%", gap: 20 }}>
          <Grid container sx={{ display: "flex", gap: "20px" }}>
            <Box
              sx={{ background: "#EEEEEE", borderRadius: "4px", padding: "5px", display: "flex", alignItems: "center" }}
            >
              <Typography sx={{ color: "#333333", fontSize: "14px", fontWeight: 700 }}>
                <span style={{ color: "#767676" }}>Start time </span>
                {secondsToTimecode(startTime)}
              </Typography>
              <input
                type="number"
                min={0}
                max={storyDuration}
                value={startTime}
                onChange={onTimeChange}
                className={classes.inputNumber}
              />
            </Box>
            <TextField
              label="Title"
              variant="outlined"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ width: "100%", marginBottom: "20px" }}
            />
            <TextField
              label="Description"
              multiline={true}
              minRows={3}
              maxRows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ width: "100%", marginBottom: "20px" }}
            />

            <TextField
              label="Keywords"
              helperText={"Enter keywords separated by commas"}
              value={keywords}
              onChange={(e) => {
                setKeywords(e.target.value);
              }}
              sx={{ width: "100%", marginBottom: "20px" }}
            />
          </Grid>
          <Box sx={{ width: "100%", height: "15px" }}></Box>
          <Grid container sx={{ alignContent: "center" }}>
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <TSSecondaryButton text="Cancel" onClick={handleCancel} styles={{ marginRight: "10px" }} />
              <TSPrimaryButton onClick={handleSave} text="Save" disabled={title?.length < 2} />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreatChapterModal;
